// src/services/common/documentService.ts
import type { BaseDocument } from '@farmfree/common/types/common/documentMeta'
import { useApi } from '@/composables/useApi'
import type { ApiResponse } from '@farmfree/common/types/apiResponse'

import { withApiSafety } from '@/utils/withApiSafety'
import { fromServerData } from '@/utils/firestore/fromServerData'
import type { COLLECTION_PERMISSIONS } from '@farmfree/common/constants/collections'

type Mode = 'admin' | 'guest'

export function createDocumentService<T>(
  collectionId: keyof typeof COLLECTION_PERMISSIONS,
  mode: Mode = 'admin' // 기본은 관리자용
) {
  const baseUrl = `api/document/${mode}/${collectionId}`

  return {
    async getOne(companyId: string, itemId: string): Promise<ApiResponse<T>> {
      const response = await withApiSafety(() =>
        useApi().get<ApiResponse<T>>(`${baseUrl}/${itemId}`, {
          params: { companyId },
        })
      )
      // 서버 Timestamp ({seconds, nanoseconds}) → Firestore Timestamp 객체로 변환
      if (response.isSuccess && response.data) {
        response.data = fromServerData(response.data) as T
      }
      return response
    },

    async getAll(companyId: string, since?: number): Promise<ApiResponse<T[]>> {
      const response = await withApiSafety(() =>
        useApi().get<ApiResponse<T[]>>(baseUrl, {
          params: { companyId, ...(since ? { since } : {}) },
        })
      )
      // 서버 Timestamp ({seconds, nanoseconds}) → Firestore Timestamp 객체로 변환
      if (response.isSuccess && response.data) {
        response.data = fromServerData(response.data) as T[]
      }
      return response
    },

    async save(companyId: string, item: T): Promise<ApiResponse> {
      return withApiSafety(() => useApi().post<ApiResponse>(baseUrl, { companyId, item }))
    },

    async saveMany(companyId: string, items: T[]): Promise<ApiResponse> {
      return withApiSafety(() =>
        useApi().post<ApiResponse>(`${baseUrl}/saveMany`, { companyId, items })
      )
    },

    async deleteItem(companyId: string, itemId: string): Promise<ApiResponse> {
      return withApiSafety(() =>
        useApi().delete<ApiResponse>(`${baseUrl}/${itemId}`, {
          params: { companyId },
        })
      )
    },

    async getDeleted(companyId: string): Promise<ApiResponse<string[]>> {
      return withApiSafety(() =>
        useApi().get<ApiResponse<string[]>>(`${baseUrl}/deleted`, {
          params: { companyId },
        })
      )
    },
  }
}
